import { useDroppable } from '@dnd-kit/core';
import { Button, Flex, ListCard, Menu, MenuItem, MenuSeparator, MenuTrigger, Plus, Text } from '@chatsdet/ui';
import { DotsThreeVertical, PencilSimple, Trash } from '@phosphor-icons/react';
import type { CredentialTemplate } from '../../../../hooks/useCredentialTemplates';
import { DraggableCredentialRow } from './DraggableCredentialRow';
import type { TestUserCredential } from './types';

type Props = {
  propertyId: string;
  template: CredentialTemplate;
  credentials: TestUserCredential[];
  onAddCredential: () => void;
  onEditPersona: () => void;
  onDeletePersona: () => void;
  onEditCredential: (credId: string) => void;
  onDeleteCredential: (cred: TestUserCredential) => void;
  onClearStorageState: (credId: string) => void;
  onUnlink: (cred: TestUserCredential) => void;
  onRefresh: () => void;
};

/**
 * One persona (credential template) with its per-environment credential
 * bindings. Acts as a drop target: dragging a credential here links it to
 * the persona.
 */
export function PersonaGroup({
  propertyId,
  template,
  credentials,
  onAddCredential,
  onEditPersona,
  onDeletePersona,
  onEditCredential,
  onDeleteCredential,
  onClearStorageState,
  onUnlink,
  onRefresh,
}: Props) {
  const { setNodeRef, isOver } = useDroppable({ id: `persona:${template.id}` });
  const bindingLabel = `${credentials.length} ${credentials.length === 1 ? 'credential' : 'credentials'}`;

  return (
    <ListCard
      ref={setNodeRef}
      title={template.name}
      titleAccessory={bindingLabel}
      description={template.description || undefined}
      action={
        <Flex align="center" gap="xs" className="shrink-0">
          <Button
            variant="secondary"
            size="sm"
            iconLeft={<Plus size={16} />}
            onPress={onAddCredential}
          >
            Add credential
          </Button>
          <MenuTrigger>
            <Button
              variant="ghost"
              size="sm"
              className="p-1.5 text-foreground/70 hover:text-foreground hover:bg-muted"
              aria-label={`Actions for persona ${template.name}`}
            >
              <DotsThreeVertical size={18} weight="bold" />
            </Button>
            <Menu
              onAction={(key) => {
                if (key === 'edit') onEditPersona();
                else if (key === 'delete') onDeletePersona();
              }}
            >
              <MenuItem id="edit">
                <PencilSimple size={14} className="mr-2" />
                Edit persona
              </MenuItem>
              <MenuSeparator />
              <MenuItem id="delete" destructive>
                <Trash size={14} className="mr-2" />
                Delete persona
              </MenuItem>
            </Menu>
          </MenuTrigger>
        </Flex>
      }
      className={`transition-colors ${isOver ? 'border-primary ring-1 ring-primary/40' : ''}`}
    >
      {credentials.length === 0 ? (
        <Text size="sm" className="text-muted-foreground italic px-3 py-3">
          No credentials yet — drag one here, or add a credential to this persona.
        </Text>
      ) : (
        credentials.map((cred, index) => (
          <DraggableCredentialRow
            key={cred.id}
            propertyId={propertyId}
            credential={cred}
            sourceTemplateId={template.id}
            index={index}
            onEdit={() => onEditCredential(cred.id)}
            onDelete={() => onDeleteCredential(cred)}
            onClearStorageState={() => onClearStorageState(cred.id)}
            onUnlink={() => onUnlink(cred)}
            onRefresh={onRefresh}
          />
        ))
      )}
    </ListCard>
  );
}
